import { Component } from '@verndale/core';
import debounce from 'lodash/debounce';

class Expandables extends Component {
  private pendingAnimations: Map<HTMLElement, number> = new Map();

  setupDefaults() {
    this.dom = {
      expandables: this.el.querySelectorAll('li'),
      content: this.el.querySelectorAll('.expandable')
    };

    this.setBlockSize();
  }

  addListeners() {
    this.el.addEventListener('click', this.handleClick.bind(this));
    window.addEventListener('resize', debounce(this.setBlockSize.bind(this), 500));
  }

  setBlockSize() {
    this.calculateHeights();
  }

  calculateHeights() {
    (this.dom.content as NodeListOf<HTMLElement>).forEach((content, index) => {
      const expandableItem = (this.dom.expandables as NodeListOf<HTMLElement>)[index];
      this.calculateSingleHeight(content, expandableItem);
    });
  }

  calculateSingleHeight(content: HTMLElement, expandableItem: HTMLElement) {
    const originalStyles = {
      blockSize: content.style.blockSize,
      overflow: content.style.overflow,
      paddingBlockEnd: content.style.paddingBlockEnd,
      position: content.style.position,
      visibility: content.style.visibility
    };

    content.style.blockSize = 'auto';
    content.style.overflow = 'visible';
    content.style.paddingBlockEnd = '25px';
    content.style.position = 'static';
    content.style.visibility = 'visible';

    content.offsetHeight;

    const height = content.scrollHeight;

    Object.assign(content.style, originalStyles);

    // Add a small safety margin to ensure no content is cut off
    expandableItem.style.setProperty('--expandable-bs', `${height + 5}px`);
  }

  handleClick(e: Event) {
    const target = e.target as HTMLElement;

    // Handle clicks on the trigger or its children (like the icon span)
    const trigger = target.closest('.trigger_expandable') as HTMLElement;
    if (trigger) {
      e.preventDefault();
      const expandableItem = trigger.closest('li') as HTMLElement;
      const content = expandableItem.querySelector('.expandable') as HTMLElement;

      // Cancel any pending animation for this item
      const pendingId = this.pendingAnimations.get(expandableItem);
      if (pendingId) {
        cancelAnimationFrame(pendingId);
        this.pendingAnimations.delete(expandableItem);
      }

      const wasOpen = expandableItem.classList.contains('open');

      if (!wasOpen) {
        // Calculate height BEFORE adding open class for smooth animation
        this.calculateSingleHeight(content, expandableItem);

        // Schedule opening animation and track it
        const animationId = requestAnimationFrame(() => {
          // Double-check it hasn't been opened by another click
          if (!expandableItem.classList.contains('open')) {
            expandableItem.classList.add('open');
          }
          this.pendingAnimations.delete(expandableItem);
        });

        this.pendingAnimations.set(expandableItem, animationId);
      } else {
        // When closing, just remove the class immediately
        expandableItem.classList.remove('open');
      }
    }
  }

  static autoInit() {
    const expandableElements = document.querySelectorAll('.mod-expandables:not([data-expandables-initialized])');
    expandableElements.forEach((element) => {
      new Expandables(element as HTMLElement);
      (element as HTMLElement).setAttribute('data-expandables-initialized', 'true');
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', Expandables.autoInit);
} else {
  Expandables.autoInit();
}

export default Expandables;
